import { useEffect, useState } from 'react';

const cache = new Map<string, string>();

/**
 * Couleur dominante d'une pochette, assombrie pour servir de « lavis »
 * derrière les en-têtes et le lecteur plein écran.
 */
export async function dominantColor(url: string): Promise<string | null> {
  if (cache.has(url)) return cache.get(url)!;
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  try {
    await img.decode();
  } catch {
    return null;
  }
  const c = document.createElement('canvas');
  c.width = c.height = 24;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, 24, 24);
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, 24, 24).data;
  } catch {
    return null;
  }
  // Moyenne pondérée par la saturation : privilégie les couleurs vives
  let r = 0, g = 0, b = 0, w = 0;
  for (let i = 0; i < data.length; i += 4) {
    const [R, G, B] = [data[i], data[i + 1], data[i + 2]];
    const max = Math.max(R, G, B), min = Math.min(R, G, B);
    const sat = max === 0 ? 0 : (max - min) / max;
    const weight = 0.15 + sat * sat * 2 + (max > 40 ? 0.2 : 0);
    r += R * weight; g += G * weight; b += B * weight; w += weight;
  }
  const [h, s] = rgbToHsl(r / w, g / w, b / w);
  // Lavis sombre et un peu saturé, lisible sous du texte clair
  const color = `hsl(${Math.round(h)} ${Math.round(Math.min(70, Math.max(18, s * 100)))}% 30%)`;
  cache.set(url, color);
  return color;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return [h, s, l];
}

export function useDominantColor(url?: string, fallback = 'hsl(250 30% 22%)') {
  const [color, setColor] = useState<string>(() => (url && cache.get(url)) || fallback);
  useEffect(() => {
    let alive = true;
    if (!url) {
      setColor(fallback);
      return;
    }
    dominantColor(url).then((c) => alive && setColor(c ?? fallback));
    return () => {
      alive = false;
    };
  }, [url, fallback]);
  return color;
}
